import { useEffect, useRef } from "react"

const AUGMENTATION_PANEL_ADD_REQUEST_EVENT = "augmentation-panel:add-request"

// Held until the add row with this key mounts and takes it, so a request made
// by the click that opens the panel is not lost.
const pendingItemKeys = new Set<string>()

/**
 * Ask a panel section's "Add …" row to open its picker, as if it had been
 * clicked. Call it from a control outside the panel (e.g. a Placeholder on
 * the page) alongside the navigation that opens the panel; it works whether
 * or not the panel is open yet.
 */
export function requestAugmentationPanelAdd(itemKey: string) {
  if (typeof window === "undefined") return
  pendingItemKeys.add(itemKey)
  window.dispatchEvent(new Event(AUGMENTATION_PANEL_ADD_REQUEST_EVENT))
}

/** Lets an add row answer {@link requestAugmentationPanelAdd} for its key. */
export function useAugmentationPanelAddRequest(
  itemKey: string,
  onRequest: () => void
) {
  const onRequestRef = useRef(onRequest)
  onRequestRef.current = onRequest
  useEffect(() => {
    const takeRequest = () => {
      if (pendingItemKeys.delete(itemKey)) onRequestRef.current()
    }
    takeRequest()
    window.addEventListener(AUGMENTATION_PANEL_ADD_REQUEST_EVENT, takeRequest)
    return () =>
      window.removeEventListener(
        AUGMENTATION_PANEL_ADD_REQUEST_EVENT,
        takeRequest
      )
  }, [itemKey])
}
